import type { StackProps } from '@mui/material/Stack';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { varAlpha } from 'src/theme/styles';
import { Iconify } from 'src/components/iconify';
import { useGetUserQuery } from 'src/generated/graphql';
import { USER_INITIALS_AVATAR_BG } from 'src/utils/name-initials';

import useAuth from 'src/auth/useAuth';

// ----------------------------------------------------------------------

type NavUserFooterProps = StackProps & {
  /** Narrow sidebar (mini) — icon-only style */
  compact?: boolean;
};

function firstInitial(name: string, email: string | undefined): string {
  const s = (name || email || '?').trim();
  return s ? s.charAt(0).toUpperCase() : '?';
}

export function NavUserFooter({ compact, sx, ...other }: NavUserFooterProps) {
  const theme = useTheme();
  const { logout } = useAuth();

  const [{ data: getUserData }] = useGetUserQuery();
  const user = getUserData?.getUser;

  const email = user?.email?.trim() || '';
  const name = user?.username?.trim() || '';
  const initial = firstInitial(name, email);

  const border = `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`;

  const onLogout = () => {
    void logout();
  };

  if (compact) {
    return (
      <Stack
        alignItems="center"
        spacing={0.5}
        sx={{
          flexShrink: 0,
          py: 1.5,
          px: 0.5,
          borderTop: (t) => `1px solid ${t.vars.palette.divider}`,
          ...sx,
        }}
        {...other}
      >
        <Avatar
          alt={name || email}
          sx={{
            width: 36,
            height: 36,
            fontSize: 15,
            fontWeight: 700,
            color: 'common.white',
            bgcolor: USER_INITIALS_AVATAR_BG,
            border,
          }}
        >
          {initial}
        </Avatar>
        <IconButton
          size="small"
          onClick={onLogout}
          aria-label="Log out"
          sx={{
            border,
            borderRadius: 2,
            color: 'text.primary',
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Iconify icon="solar:logout-2-line-duotone" width={22} />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        flexShrink: 0,
        px: 2,
        py: 1.75,
        borderTop: (t) => `1px solid ${t.vars.palette.divider}`,
        ...sx,
      }}
      {...other}
    >
      <Avatar
        alt={name || email}
        sx={{
          width: 40,
          height: 40,
          fontSize: 16,
          fontWeight: 700,
          color: 'common.white',
          bgcolor: USER_INITIALS_AVATAR_BG,
          border,
        }}
      >
        {initial}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            color: 'var(--layout-nav-text-primary-color, text.primary)',
            fontWeight: 600,
            lineHeight: 1.2,
            display: 'block',
          }}
        >
          {email || name || '—'}
        </Typography>
      </Box>
      <IconButton
        onClick={onLogout}
        aria-label="Log out"
        sx={{
          flexShrink: 0,
          border,
          borderRadius: 2,
          width: 40,
          height: 40,
          color: 'text.primary',
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Iconify icon="solar:logout-2-line-duotone" width={22} />
      </IconButton>
    </Stack>
  );
}
